import React, { useState } from 'react';
import quizData from '../jsons/Multiple_choices.json';
import MathDisplay from './MathDisplay';
import IntelligentQuiz from './IntelligentQuiz';
import { usePremium } from '../contexts/PremiumContext';

const QuizPage = ({ onShowPayment }) => {
  const { isPremium: userHasPremium, getPremiumStatusInfo } = usePremium();
  const [selectedChapter, setSelectedChapter] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [questionsPerSession] = useState(10); // Limit questions per session
  const [intelligentQuizMode, setIntelligentQuizMode] = useState(false);

  // Get available chapters from the JSON (excluding metadata)
  const chapters = Object.keys(quizData).filter(key => key !== 'metadata');

  // Protection helper functions
  const isChapterProtected = React.useCallback((chapterName) => {
    return quizData.metadata && 
           quizData.metadata[chapterName] && 
           quizData.metadata[chapterName].protected === true;
  }, []);

  const canUserAccessChapter = React.useCallback((chapterName) => {
    return !isChapterProtected(chapterName) || userHasPremium;
  }, [isChapterProtected, userHasPremium]);

  const getChapterMetadata = React.useCallback((chapterName) => {
    return quizData.metadata && quizData.metadata[chapterName] 
      ? quizData.metadata[chapterName] 
      : { title: chapterName, protected: false, description: '', price: null };
  }, []);
  
  // Function to shuffle array using Fisher-Yates algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  // Get current questions (always use shuffled/random questions)
  const currentChapterQuestions = selectedChapter ? shuffledQuestions : [];
  const currentQuestion = currentChapterQuestions[currentQuestionIndex];

  const handleChapterSelect = (chapter) => {
    // Check if the chapter is protected and user doesn't have premium access
    if (isChapterProtected(chapter) && !userHasPremium) {
      console.log('Protected chapter accessed:', chapter, 'onShowPayment available:', !!onShowPayment);
      if (onShowPayment) {
        onShowPayment();
      }
      return;
    }

    // User can access this chapter
    setSelectedChapter(chapter);
    setCurrentQuestionIndex(0);
    setSelectedAnswer('');
    setShowResult(false);
    setShowHint(false);
    setScore(0);
    setTotalQuestions(0);
    setIntelligentQuizMode(false);
    
    // Always shuffle questions and limit to questionsPerSession
    const allQuestions = quizData[chapter] || [];
    const shuffled = shuffleArray(allQuestions);
    const limitedQuestions = shuffled.slice(0, Math.min(questionsPerSession, shuffled.length));
    setShuffledQuestions(limitedQuestions);
  };

  const handleAnswerSelect = (answer) => {
    if (showResult) return;
    setSelectedAnswer(answer);
  };

  const handleSubmitAnswer = () => {
    if (!selectedAnswer || showResult) return;

    const isCorrect = selectedAnswer === currentQuestion.right_answer;
    setShowResult(true);
    
    if (isCorrect) {
      // Correct answer
      setScore(score + 1);
    } else {
      // Wrong answer
      const topic = currentQuestion.topic;
      if (topic) {
        const userPerformance = JSON.parse(localStorage.getItem('userPerformance')) || {};
        userPerformance[topic] = (userPerformance[topic] || 0) + 1;
        localStorage.setItem('userPerformance', JSON.stringify(userPerformance));
      }
    }
    setTotalQuestions(totalQuestions + 1);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < currentChapterQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer('');
      setShowResult(false);
      setShowHint(false);
    } else {
      // Quiz completed
      alert(`Quiz လုံးလုံးပြီးသွားပါပြီ! သင်၏ ရမှတ်: ${score}/${totalQuestions}`);
    }
  };

  const toggleHint = () => {
    setShowHint(!showHint);
  };

  const startIntelligentQuiz = () => {
    setSelectedChapter('');
    setIntelligentQuizMode(true);
  };

  if (intelligentQuizMode) {
    return <IntelligentQuiz onExit={() => setIntelligentQuizMode(false)} onShowPayment={onShowPayment} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex">
      {/* Sidebar */}
      <div className="w-1/4 bg-white shadow-lg border-r border-gray-200">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="flex items-center space-x-3 mb-2">
            <h2 className="text-xl font-bold text-white">📚 Quiz Chapters</h2>
            {userHasPremium && (
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold" title={getPremiumStatusInfo().message}>
                ✨ Premium
              </div>
            )}
          </div>
          <p className="text-blue-100 text-sm">
            Quiz လုပ်ရန် အခန်းတစ်ခုကို ရွေးချယ်ပါ
            {!userHasPremium && (
              <span className="block text-blue-200 text-xs mt-1">
                💡 Premium chapters require subscription
              </span>
            )}
          </p>
        </div>
        
        <div className="p-4 space-y-2">
          <button
            onClick={startIntelligentQuiz}
            className={`w-full text-left p-4 rounded-lg transition-all duration-200 border relative ${
              !userHasPremium
                ? 'border-orange-200 bg-gradient-to-br from-gray-50 to-orange-50 hover:border-orange-300'
                : 'bg-orange-100 border-orange-300 text-orange-800 shadow-md hover:bg-orange-200'
            }`}
          >
            {/* Premium icon for intelligent quiz */}
            {!userHasPremium && (
              <div className="absolute top-2 right-2">
                <div className="bg-orange-100 p-1 rounded-full">
                  <svg className="w-3 h-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
            )}
            
            <div className="flex items-center">
              <span className="text-lg mr-3">
                {!userHasPremium ? '🔒' : '🧠'}
              </span>
              <div className="flex-1">
                <h3 className="font-semibold text-sm">Intelligent Quiz</h3>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">
                    {userHasPremium 
                      ? 'intelligent personalized practice'
                      : 'Premium subscription required'
                    }
                  </p>
                  <span className={`text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700`}>
                    💎 Premium
                  </span>
                </div>
              </div>
            </div>
          </button>
          {chapters.map((chapter, index) => {
            const isProtected = isChapterProtected(chapter);
            const canAccess = canUserAccessChapter(chapter);
            const chapterMetadata = getChapterMetadata(chapter);
            
            return (
              <button
                key={index}
                onClick={() => handleChapterSelect(chapter)}
                className={`w-full text-left p-4 rounded-lg transition-all duration-200 border relative ${
                  isProtected && !canAccess
                    ? 'border-orange-200 bg-gradient-to-br from-gray-50 to-orange-50 hover:border-orange-300'
                    : selectedChapter === chapter
                      ? 'bg-blue-50 border-blue-200 text-blue-800 shadow-md transform scale-105'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                }`}
              >
                {/* Lock icon for protected content */}
                {isProtected && !canAccess && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-orange-100 p-1 rounded-full">
                      <svg className="w-3 h-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                  </div>
                )}

                <div className="flex items-center">
                  <span className="text-lg mr-3">
                    {isProtected && !canAccess 
                      ? '🔒' 
                      : selectedChapter === chapter 
                        ? '📖' 
                        : '🎲'
                    }
                  </span>
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm">{chapterMetadata.title || chapter}</h3>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-gray-500">
                        {canAccess 
                          ? `${Math.min(questionsPerSession, (quizData[chapter] || []).length)} questions`
                          : 'Premium subscription required'
                        }
                      </p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        isProtected 
                          ? 'bg-orange-100 text-orange-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {isProtected ? '💎 Premium' : '🎲 Free'}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Score Display */}
        {totalQuestions > 0 && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {score}/{totalQuestions}
              </div>
              <div className="text-sm text-gray-600">ရမှတ်</div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8">
        {!selectedChapter ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-6xl mb-4">📚</div>
              <h3 className="text-2xl font-bold text-gray-700 mb-2">
                Calculus Quiz ကိုကြိုဆိုပါတယ်!
              </h3>
              <p className="text-gray-600">
                ဘယ်ဘက်မှ အခန်းတစ်ခုကို ရွေးချယ်ပြီး quiz စတင်ပါ
              </p>
            </div>
          </div>
        ) : !currentQuestion ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-gray-700 mb-2">
                ဤအခန်းတွင် မေးခွန်းများ မရှိပါ
              </h3>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            {/* Check if selected chapter is protected and user doesn't have access */}
            {!canUserAccessChapter(selectedChapter) ? (
              <div className="text-center py-16">
                <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-8 max-w-md mx-auto">
                  <div className="text-6xl mb-4">🔒</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Premium Content</h3>
                  <p className="text-gray-600 mb-4">
                    This chapter requires a premium subscription to access quiz questions.
                  </p>
                  <button
                    onClick={() => onShowPayment && onShowPayment()}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium py-3 px-6 rounded-lg transition-all transform hover:scale-105"
                  >
                    🔓 Upgrade to Premium
                  </button>
                </div>
              </div>
            ) : (
              <>
            {/* Question Header */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    မေးခွန်း {currentQuestionIndex + 1}/{currentChapterQuestions.length}
                  </div>
                  <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                    <span>🎲</span>
                    <span>Random Quiz</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700">
                    {selectedChapter}
                  </h3>
                </div>
                
                {/* Hint Button */}
                <button
                  onClick={toggleHint}
                  className="flex items-center space-x-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors"
                >
                  <span className="text-lg">💡</span>
                  <span className="text-sm font-medium">Hint</span>
                </button>
              </div>

              {/* Question */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="text-lg text-gray-800">
                  <MathDisplay latex={currentQuestion.question} />
                </div>
              </div>

              {/* Hint Display */}
              {showHint && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-2">
                    <span className="text-yellow-600 text-lg">💡</span>
                    <div className="text-yellow-800 flex-1">
                      <MathDisplay latex={currentQuestion.hint} />
                    </div>
                  </div>
                </div>
              )}

              {/* Answer Choices */}
              <div className="space-y-3 mb-6">
                {currentQuestion.multiple_choices.map((choice, index) => {
                  const isCorrectAnswer = choice === currentQuestion.right_answer;
                  const isSelected = selectedAnswer === choice;
                  const isDisabled = showResult;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => !isDisabled ? handleAnswerSelect(choice) : null}
                      disabled={isDisabled}
                      className={`w-full text-left p-4 rounded-lg border transition-all duration-200 ${
                        isSelected
                          ? showResult
                            ? isCorrectAnswer
                              ? 'bg-green-50 border-green-300 text-green-800'
                              : 'bg-red-50 border-red-300 text-red-800'
                            : 'bg-blue-50 border-blue-300 text-blue-800'
                          : showResult && isCorrectAnswer
                          ? 'bg-green-50 border-green-300 text-green-800'
                          : isDisabled
                          ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 cursor-pointer hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? showResult
                              ? isCorrectAnswer
                                ? 'border-green-500 bg-green-500'
                                : 'border-red-500 bg-red-500'
                              : 'border-blue-500 bg-blue-500'
                            : showResult && isCorrectAnswer
                            ? 'border-green-500 bg-green-500'
                            : 'border-gray-300'
                        }`}>
                          {(isSelected || (showResult && isCorrectAnswer)) && (
                            <span className="text-white text-sm">
                              {showResult && isCorrectAnswer ? '✓' : 
                               showResult && isSelected && !isCorrectAnswer ? '✗' : '✓'}
                            </span>
                          )}
                        </div>
                        <div className="flex-1">
                          <MathDisplay latex={choice} inline={true} />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Submit/Next Button */}
              {!showResult ? (
                <button
                  onClick={handleSubmitAnswer}
                  disabled={!selectedAnswer}
                  className={`w-full py-3 rounded-lg font-medium transition-colors ${
                    selectedAnswer
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  အဖြေပေးပါ
                </button>
              ) : (
                <div className="space-y-4">
                  {/* Result Message */}
                  <div className={`p-4 rounded-lg ${
                    selectedAnswer === currentQuestion.right_answer
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-2xl">
                        {selectedAnswer === currentQuestion.right_answer ? '🎉' : '❌'}
                      </span>
                      <h4 className={`font-bold ${
                        selectedAnswer === currentQuestion.right_answer
                          ? 'text-green-800'
                          : 'text-red-800'
                      }`}>
                        {selectedAnswer === currentQuestion.right_answer 
                          ? 'မှန်ကန်ပါသည်!' 
                          : 'မမှန်ကန်ပါ!'
                        }
                      </h4>
                    </div>
                    
                    {/* Show explanation */}
                    {(showResult) && (
                      <div className={`${
                        selectedAnswer === currentQuestion.right_answer
                          ? 'text-green-700'
                          : 'text-red-700'
                      }`}>
                        <p className="font-medium mb-2">ရှင်းလင်းချက်:</p>
                        <div className="text-sm">
                          <MathDisplay latex={currentQuestion.reason} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    {/* Next Question Button - show if correct or max attempts reached */}
                    {(showResult) && (
                      <button
                        onClick={handleNextQuestion}
                        className="flex-1 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                      >
                        {currentQuestionIndex < currentChapterQuestions.length - 1 
                          ? 'နောက်မေးခွန်းသို့' 
                          : 'Quiz ပြီးဆုံး'
                        }
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizPage;